/**
 * KanbanColumn (SPEC E3 - pipeline-and-capi, R2).
 *
 * One pipeline stage column. The header shows the E6 stage label + count +
 * total value (from the summary). Open stages are @dnd-kit droppable targets and
 * render their DealCards. Terminal stages (closed_won / closed_lost) render
 * COLLAPSED by default - header with count only, no cards, no drop target - 
 * and expand on header click (R2 AC5).
 */

import { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { DealCard, formatValue } from './DealCard';
import type {
  Opportunity,
  PipelineSummaryStage,
} from '@/types/pipeline';

interface KanbanColumnProps {
  stage: PipelineSummaryStage;
  opportunities: Opportunity[];
  currency: string;
  loading?: boolean;
  onCardClick?: (opp: Opportunity) => void;
  // R3c multi-select threading.
  selectedIds?: Set<string>;
  onCardSelectChange?: (id: string, checked: boolean) => void;
}

export function KanbanColumn({
  stage,
  opportunities,
  currency,
  loading,
  onCardClick,
  selectedIds,
  onCardSelectChange,
}: KanbanColumnProps) {
  const isTerminal = stage.is_terminal;
  // Terminal columns start collapsed; open columns are always expanded.
  const [expanded, setExpanded] = useState(!isTerminal);

  // A collapsed terminal column is NOT a drop target (R2 AC5).
  const droppableDisabled = isTerminal && !expanded;
  const { setNodeRef, isOver } = useDroppable({
    id: stage.stage_key,
    disabled: droppableDisabled,
    data: { stageKey: stage.stage_key },
  });

  const accent = stage.color || '#94a3b8';

  return (
    <div
      className="flex flex-col w-[280px] shrink-0"
      data-testid={`kanban-column-${stage.stage_key}`}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => isTerminal && setExpanded((v) => !v)}
        className={cn(
          'flex items-center justify-between gap-2 px-3 py-2 rounded-t-md border-b-2 text-left',
          isTerminal ? 'cursor-pointer bg-muted/50' : 'bg-muted/30 cursor-default'
        )}
        style={{ borderBottomColor: accent }}
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-1.5 min-w-0">
          {isTerminal &&
            (expanded ? (
              <ChevronDown className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0" />
            ))}
          <span
            className="h-2.5 w-2.5 rounded-full shrink-0"
            style={{ backgroundColor: accent }}
          />
          <span className="text-sm font-semibold truncate">{stage.label}</span>
          <Badge variant="secondary" className="shrink-0">
            {stage.count}
          </Badge>
        </span>
        <div className="flex flex-col items-end shrink-0">
          <span className="text-xs font-medium">
            {formatValue(stage.total_value, currency)}
          </span>
          {!isTerminal && stage.weighted_value > 0 && (
            <span className="text-[11px] text-muted-foreground">
              wt {formatValue(stage.weighted_value, currency)}
            </span>
          )}
          {stage.stale_count > 0 && (
            <span
              className="text-[11px] text-amber-600 font-medium"
              data-testid={`stale-count-${stage.stage_key}`}
            >
              {stage.stale_count} rotting
            </span>
          )}
        </div>
      </button>

      {/* Body */}
      {expanded && (
        <div
          ref={setNodeRef}
          className={cn(
            'flex-1 min-h-[120px] p-2 rounded-b-md bg-muted/10 border border-t-0',
            isOver && 'ring-2 ring-primary/50 bg-primary/5'
          )}
          data-testid={`column-body-${stage.stage_key}`}
        >
          {loading ? (
            <p className="text-xs text-muted-foreground px-1 py-2">Loading…</p>
          ) : opportunities.length === 0 ? (
            <p className="text-xs text-muted-foreground px-1 py-2">No deals</p>
          ) : (
            opportunities.map((opp) => (
              <DealCard
                key={opp.id}
                opportunity={opp}
                rottingDays={stage.rotting_days}
                currency={currency}
                onClick={onCardClick}
                selected={selectedIds?.has(opp.id)}
                onSelectChange={onCardSelectChange}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
